import { useCallback, useEffect, useMemo, useState } from 'react';
import type { BedConfig, BedType, NewRoom, Property, Room, RoomBed, RoomConfigPatch, RoomType } from '@lemontaigu/pms-core';
import { CAPABILITIES } from '@lemontaigu/platform';
import { useCapability } from '@lemontaigu/platform-ui';
import { PerEtablissement, usePms, usePmsReach, usePmsScope, type PmsBundle } from './PmsProvider.js';
import { useAskEtablissement } from './CreateTarget.js';
import { Catalogue } from './Catalogue.js';
import { Conditions } from './Conditions.js';
import { ChannexMapping } from './ChannexMapping.js';
import { RoomSheet } from './RoomSheet.js';
import { RoomCreate } from './RoomCreate.js';
import { BathIcon, BedIcons, OutlookIcon, WcIcon } from './RoomIcons.js';
import {
  EMPTY_ROOM_CONFIG_FILTERS,
  ROOM_CONFIG_PRESETS,
  filterRoomConfig,
  roomConfigOptions,
  roomConfigRows,
  sortByRoomCategory,
  sortRoomConfig,
  sortRoomConfigPreset,
  type RoomConfigFilters,
  type RoomConfigPreset,
  type RoomConfigRow,
  type RoomConfigSortKey,
} from './logic.js';

// « Configuration hôtel » (PMS) — the hotel's room reference: one row per
// physical room, read from pms_room + pms_room_type. Every column sorts; Type,
// Catégorie and Zone chauffage filter; three preset orders (logic.ts).
//
// THIS TABLE IS THE OFFICIAL VIEW OF THE SOURCE OF TRUTH (D38, 2026-08-06).
// `Configuration Chambre.xlsx` seeded it (migrations 0058/0059) and is now a
// historical import — do NOT re-seed from it, that would revert corrections
// the DB has taken since. Column order still follows the sheet, because that
// is the order the operator reads a room in.
//
// EDITABLE since the room-edit brief increment 2: a row click opens the « fiche
// chambre » (RoomSheet.tsx), which writes the whole row through ONE RPC. Before
// that, correcting a room meant a migration and a dispatch — the cost D38
// created by making this table the source of truth. Increment 3 added creating
// a room and deleting a never-used one; renumbering stays impossible by design
// (E5 — the number is the identity reservations point at).

// The pictogram columns lead (operator, 2026-08-06) — a row is read by its
// glyphs first, and the text columns confirm. « Vue », « Douche » and « WC NS »
// are gone as text: their glyph carries the same fact, and all stay searchable.
//
// `band` marks the cells tinted by capacity (operator, 2026-08-06): N°, Nbre,
// Catégorie and Type share one background per occupancy, so a room's size is
// legible as a block rather than read off the Nbre digit.
const COLUMNS: {
  key: RoomConfigSortKey;
  label: string;
  title?: string;
  align?: 'left' | 'center';
  band?: boolean;
}[] = [
  { key: 'bedsRank', label: 'Lits', title: 'King 180 · double 160 · simple — trié par capacité' },
  { key: 'outlookKey', label: 'Vue', title: 'Balcon (chambre SUP), sinon la vue : château, jardin, ville', align: 'center' },
  { key: 'hasShower', label: 'Bain', title: 'Douche à l’italienne ou baignoire', align: 'center' },
  { key: 'wcNotSeparated', label: 'WC', title: 'WC séparé (pièce dédiée) ou non séparé', align: 'center' },
  { key: 'roomNumber', label: 'N°', band: true },
  { key: 'maxOccupancy', label: 'Nbre', title: 'Nombre de personnes maximum', align: 'center', band: true },
  { key: 'categoryCode', label: 'Catégorie', band: true },
  {
    key: 'currentBedConfig',
    label: 'Config.',
    title:
      'Configurations possibles de la chambre, et celle en place quand elle est connue',
    band: true,
  },
  { key: 'isSuperior', label: 'SUP', title: 'Chambre supérieure', align: 'center' },
  { key: 'heatingZone', label: 'Zone CH', title: 'Zone de chauffage', align: 'center' },
  { key: 'bathroomHeatingZone', label: 'Zone SdB', title: 'Zone de chauffage salle de bain', align: 'center' },
  { key: 'remarks', label: 'Remarques' },
];

/** The établissement column, shown only when several are in reach (U3). */
const ETAB_COLUMN: (typeof COLUMNS)[number] = { key: 'propertyCode', label: 'Établ.', title: 'Établissement' };

/** One établissement's part of the table: its units, and the vocabularies its
 *  fiche reads — never another building's (two « DSTD » are two products). */
interface Source {
  property: Property | null;
  services: PmsBundle['services'];
  rooms: Room[];
  types: RoomType[];
  bedConfigs: BedConfig[];
  bedTypes: BedType[];
  roomBeds: RoomBed[];
}

export function Configuration() {
  const pms = usePms();
  // Which KIND of établissement is in scope decides what « Nouveau… » creates.
  // Read from the property registry rather than guessed from the existing
  // units: an apartments property with nothing in it yet must still offer the
  // apartment form, which is exactly the case the operator needs for Apt0.
  const { properties, current } = usePmsScope();
  const apartments = properties.find((p) => p.code === current)?.kind === 'apartments';
  // U3: every établissement in reach, each read through its own bundle. With
  // one, that bundle IS the screen's établissement and the table is unchanged;
  // without a property list (a failed read), the scoped services, as before.
  const reach = usePmsReach();
  const multi = reach.length > 1;
  // « logements » once the table mixes rooms and apartments.
  const logements = multi || apartments;
  const [data, setData] = useState<Source[] | null>(null);
  const [filters, setFilters] = useState<RoomConfigFilters>(EMPTY_ROOM_CONFIG_FILTERS);
  // Two ways to order the table, one at a time: a named preset (the operator's
  // reading orders) or a column click. Picking either drops the other, so the
  // header arrow and the active chip can never disagree about what is applied.
  const [preset, setPreset] = useState<RoomConfigPreset | null>('defaut');
  const [sortKey, setSortKey] = useState<RoomConfigSortKey>('roomNumber');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [err, setErr] = useState('');
  // The fiche chambre (brief E4). Held by ID, not by object: after a save the
  // rooms are refetched, and a stale object would show pre-save values.
  const [openRoomId, setOpenRoomId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState('');
  const canEdit = useCapability(CAPABILITIES.PMS_ROOM_WRITE);
  // U2: a new unit has no anchor, so the user names its établissement — and
  // the form then shows THAT building's kind, categories and bed list, read
  // and written through its own bundle, never the screen's scope.
  const [createIn, setCreateIn] = useState<{
    bundle: PmsBundle;
    types: RoomType[];
    bedConfigs: BedConfig[];
  } | null>(null);
  const { ask, picker } = useAskEtablissement();

  const load = useCallback(async () => {
    setErr('');
    const from: { property: Property | null; services: PmsBundle['services'] }[] =
      reach.length > 0
        ? reach.map((b) => ({ property: b.property, services: b.services }))
        : [{ property: null, services: pms }];
    const read = await Promise.all(
      from.map(async (f): Promise<Source | string> => {
        const [rms, tps, beds, btypes, spaces] = await Promise.all([
          f.services.reference.rooms(),
          f.services.reference.roomTypes(),
          f.services.reference.bedConfigs(),
          f.services.reference.bedTypes(),
          // Per-space beds for the multi-space units. Empty for the hotel, so
          // this list costs one query and changes nothing there.
          f.services.reference.roomBeds(),
        ]);
        const bad = [rms, tps, beds, btypes, spaces].find((r) => !r.ok);
        if (bad && !bad.ok) return bad.error.message;
        return {
          ...f,
          rooms: rms.ok ? rms.value : [],
          // Canonical category order — the fiche chambre and the create form
          // read from this list, so their selects match every other one.
          types: tps.ok ? sortByRoomCategory(tps.value) : [],
          bedConfigs: beds.ok ? beds.value : [],
          bedTypes: btypes.ok ? btypes.value : [],
          roomBeds: spaces.ok ? spaces.value : [],
        };
      }),
    );
    const failed = read.find((r): r is string => typeof r === 'string');
    if (failed !== undefined) {
      setErr(failed);
      return;
    }
    setData(read as Source[]);
  }, [pms, reach]);

  useEffect(() => {
    void load();
  }, [load]);

  const allRows = useMemo(
    () => (data ?? []).flatMap((d) => roomConfigRows(d.rooms, d.types, d.bedConfigs)),
    [data],
  );
  const options = useMemo(() => roomConfigOptions(allRows), [allRows]);
  const rows = useMemo(() => {
    const kept = filterRoomConfig(allRows, filters);
    const sorted = preset === null ? sortRoomConfig(kept, sortKey, sortDir) : sortRoomConfigPreset(kept, preset);
    // A preset reads établissement by établissement, in their order (a stable
    // sort keeps the preset's order within each). A column click is the
    // user's own order and wins.
    if (!multi || preset === null) return sorted;
    const rank = new Map(reach.map((b, i) => [b.property.code, i]));
    return [...sorted].sort((a, b) => (rank.get(a.propertyCode) ?? 0) - (rank.get(b.propertyCode) ?? 0));
  }, [allRows, filters, preset, sortKey, sortDir, multi, reach]);

  // The open unit's own établissement: its fiche reads THAT building's
  // categories and beds, and its writes go through THAT bundle (U3, UD2).
  const openSource = (data ?? []).find((d) => d.rooms.some((r) => r.id === openRoomId)) ?? null;
  const openRoom = openSource?.rooms.find((r) => r.id === openRoomId) ?? null;
  // Grouped here rather than in the sheet: the sheet edits ONE unit and should
  // not have to know the shape of the whole property's bed list. Stable
  // identity matters — the sheet resets its draft when this prop changes.
  const openRoomBeds = useMemo(
    () => (openSource ? openSource.roomBeds.filter((b) => b.roomId === openRoomId) : []),
    [openSource, openRoomId],
  );

  const save = async (patch: RoomConfigPatch) => {
    if (openRoomId === null || openSource === null) return;
    setSaving(true);
    setSaveErr('');
    const result = await openSource.services.reference.updateRoomConfig(openRoomId, patch);
    setSaving(false);
    if (!result.ok) {
      // The O-D refusal lands here, naming the reservations to reassign — it
      // must reach the operator verbatim, not as a generic failure.
      setSaveErr(result.error.message);
      return;
    }
    setOpenRoomId(null);
    await load();
  };

  const startCreate = async () => {
    setSaveErr('');
    setOpenRoomId(null);
    const target = await ask('Nouveau logement');
    if (target === null) return;
    const [tps, beds] = await Promise.all([
      target.services.reference.roomTypes(),
      target.services.reference.bedConfigs(),
    ]);
    if (!tps.ok || !beds.ok) {
      setErr(!tps.ok ? tps.error.message : !beds.ok ? beds.error.message : '');
      return;
    }
    setCreateIn({ bundle: target, types: sortByRoomCategory(tps.value), bedConfigs: beds.value });
  };

  const create = async (draft: NewRoom) => {
    if (createIn === null) return;
    setSaving(true);
    setSaveErr('');
    const result = await createIn.bundle.services.reference.createRoom(draft);
    setSaving(false);
    if (!result.ok) {
      setSaveErr(result.error.message);
      return;
    }
    setCreateIn(null);
    // The table lists every établissement in reach (U3), so the new unit is
    // always on screen — wherever it was created.
    await load();
    // Open the new room straight away: creation deliberately asks for only the
    // few fields that make a row readable, so the fiche is the next step.
    setOpenRoomId(result.value);
  };

  const remove = async () => {
    if (openRoomId === null || openSource === null) return;
    setSaving(true);
    setSaveErr('');
    const result = await openSource.services.reference.deleteRoom(openRoomId);
    setSaving(false);
    if (!result.ok) {
      // "Already used — block it instead of deleting" arrives here, and is the
      // whole point of the refusal: it must be read, not swallowed.
      setSaveErr(result.error.message);
      return;
    }
    setOpenRoomId(null);
    await load();
  };

  const columns = multi ? [ETAB_COLUMN, ...COLUMNS] : COLUMNS;

  // Sum of « Nbre » = capacity in persons. Deliberately NOT the xlsx summary's
  // « Lits » total (115), which is internally inconsistent (its 3-person block
  // reads 17 rooms → 45) — this recomputes from the per-room figures.
  const capacity = rows.reduce((s, r) => s + (r.maxOccupancy ?? 0), 0);
  const filtered = rows.length !== allRows.length;

  const toggleSort = (key: RoomConfigSortKey) => {
    if (key === sortKey && preset === null) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir('asc');
    }
    setPreset(null); // a column click takes over from the preset
  };

  const set = (patch: Partial<RoomConfigFilters>) => setFilters((f) => ({ ...f, ...patch }));

  const cell = (row: RoomConfigRow, key: RoomConfigSortKey) => {
    switch (key) {
      case 'propertyCode':
        return reach.find((b) => b.property.code === row.propertyCode)?.property.labelFr ?? row.propertyCode;
      case 'roomNumber':
        return <strong>{row.roomNumber}</strong>;
      case 'categoryCode':
        return row.categoryCode === null ? (
          <span className="pms-cfg-none" title="Chambre sans catégorie">
            —
          </span>
        ) : (
          // The code alone (operator, 2026-08-07) — 3–4 letters the operator
          // reads fluently; the French label would double the column's width to
          // repeat what the code already says. It stays on the tooltip, and
          // stays searchable (filterRoomConfig still matches on the label).
          <strong className="pms-cfg-cat" title={row.categoryLabel ?? undefined}>
            {row.categoryCode}
          </strong>
        );
      case 'currentBedConfig':
        // The SET, not one code. A convertible room is « Double / Twin », and
        // the standing one is emphasised when anyone has said which it is.
        return (
          <span className="pms-cfg-opts">
            {row.configOptions.length === 0
              ? '—'
              : row.configOptions.map((code, i) => (
                  <span key={code}>
                    {i > 0 && <span className="pms-cfg-sep"> / </span>}
                    <span className={code === row.currentBedConfig ? 'is-current' : ''}>{code}</span>
                  </span>
                ))}
          </span>
        );
      case 'bedsRank':
        return (
          <BedIcons
            layouts={row.bedLayouts}
            doubleBedType={row.doubleBedType}
            current={row.currentBedConfig}
          />
        );
      case 'outlookKey':
        return <OutlookIcon kind={row.outlook} isSuperior={row.isSuperior} view={row.view} />;
      case 'hasShower':
        return <BathIcon hasShower={row.hasShower} />;
      case 'wcNotSeparated':
        return <WcIcon notSeparated={row.wcNotSeparated} />;
      case 'isSuperior':
        return row.isSuperior ? <span className="pms-room-sup">SUP</span> : '';
      case 'heatingZone':
      case 'bathroomHeatingZone': {
        const zone = row[key];
        if (zone === null) return '';
        return <span className={`pms-zone is-${zone}`}>{zone}</span>;
      }
      case 'remarks':
        return row.remarks ?? '';
      default: {
        const v = row[key];
        return v === null || v === '' ? '' : String(v);
      }
    }
  };

  return (
    <div className="pms">
      <div className="pms-head">
        {/* Both of these were written when HOTEL was the only établissement, so
            they hard-coded its noun AND its count — the apartments property
            showed « Configuration hôtel — chambres · les 42 chambres » above a
            list of 3 apartments (operator, 2026-08-11). The count now comes
            from the rows on screen rather than from a literal, which is also
            what stops it going stale the next time a unit is added. */}
        <h2 className="section-title">
          {multi
            ? 'Configuration — chambres et logements'
            : apartments
              ? 'Configuration — logements'
              : 'Configuration hôtel — chambres'}
        </h2>
        <span className="pms-hint">
          Configuration physique des {allRows.length}{' '}
          {logements ? (allRows.length > 1 ? 'logements' : 'logement') : 'chambres'} — référence officielle.{' '}
          {canEdit ? 'Cliquez une ligne pour ouvrir la fiche et la modifier.' : 'Lecture seule.'} Lits : king 180 ·
          double 160 · simple ; ⇄ = convertible.
        </span>
      </div>

      {err && <p className="auth-err">Erreur : {err}</p>}

      <div className="pms-cfg-sorts">
        <span className="pms-cfg-sorts-label">Tri</span>
        {ROOM_CONFIG_PRESETS.map((p) => (
          <button
            key={p.id}
            type="button"
            title={p.title}
            className={`pms-chip ${preset === p.id ? 'is-on' : ''}`}
            onClick={() => setPreset(p.id)}
          >
            {p.label}
          </button>
        ))}
        {canEdit && (
          <button
            type="button"
            className="pms-chip pms-chip-add"
            title={
              properties.length > 1
                ? 'Ajouter une chambre ou un appartement'
                : apartments
                  ? 'Ajouter un appartement'
                  : 'Ajouter une chambre'
            }
            onClick={() => void startCreate()}
          >
            {properties.length > 1 ? '+ Logement' : apartments ? '+ Appartement' : '+ Chambre'}
          </button>
        )}
        {preset === null && (
          <span className="pms-cfg-sorts-manual">
            colonne « {columns.find((c) => c.key === sortKey)?.label ?? sortKey} »{' '}
            {sortDir === 'asc' ? '▲' : '▼'}
          </span>
        )}
      </div>

      <div className="pms-cfg-filters">
        {multi && (
          <label>
            Établissement
            <select value={filters.etablissement} onChange={(e) => set({ etablissement: e.target.value })}>
              <option value="">Tous</option>
              {reach.map((b) => (
                <option key={b.property.code} value={b.property.code}>
                  {b.property.labelFr}
                </option>
              ))}
            </select>
          </label>
        )}
        <label>
          Type
          <select value={filters.bedConfig} onChange={(e) => set({ bedConfig: e.target.value })}>
            <option value="">Tous</option>
            {options.bedConfigs.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </select>
        </label>
        <label>
          Catégorie
          <select value={filters.category} onChange={(e) => set({ category: e.target.value })}>
            <option value="">Toutes</option>
            {options.categories.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </select>
        </label>
        <label>
          Zone chauffage
          <select value={filters.heatingZone} onChange={(e) => set({ heatingZone: e.target.value })}>
            <option value="">Toutes</option>
            {options.heatingZones.map((v) => (
              <option key={v} value={v}>
                Zone {v}
              </option>
            ))}
          </select>
        </label>
        <label className="pms-cfg-search">
          Recherche
          <input
            type="search"
            value={filters.search}
            placeholder="N°, vue, remarque…"
            onChange={(e) => set({ search: e.target.value })}
          />
        </label>
        {(filtered || filters.search !== '') && (
          <button type="button" className="pms-chip" onClick={() => setFilters(EMPTY_ROOM_CONFIG_FILTERS)}>
            Réinitialiser
          </button>
        )}
      </div>

      <div className="pms-table-wrap">
        <table className="pms-cfg">
          <thead>
            <tr>
              {columns.map((c) => (
                <th
                  key={c.key}
                  title={c.title}
                  className={`${c.align === 'center' ? 'is-center' : ''} ${
                    preset === null && sortKey === c.key ? 'is-sorted' : ''
                  }`}
                >
                  <button type="button" onClick={() => toggleSort(c.key)}>
                    {c.label}
                    <span className="pms-cfg-arrow">
                      {preset === null && sortKey === c.key ? (sortDir === 'asc' ? '▲' : '▼') : '↕'}
                    </span>
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data === null && (
              <tr>
                <td colSpan={columns.length} className="pms-empty">
                  Chargement…
                </td>
              </tr>
            )}
            {data !== null && rows.length === 0 && (
              <tr>
                <td colSpan={columns.length} className="pms-empty">
                  {logements
                    ? 'Aucun logement ne correspond aux filtres.'
                    : 'Aucune chambre ne correspond aux filtres.'}
                </td>
              </tr>
            )}
            {rows.map((row) => (
              <tr
                key={row.id}
                className={`pms-cfg-row ${row.id === openRoomId ? 'is-open' : ''}`}
                onClick={() => setOpenRoomId(row.id)}
                title={`${canEdit ? 'Ouvrir' : 'Voir'} la fiche ${logements ? 'logement' : 'chambre'}`}
              >
                {columns.map((c) => (
                  <td
                    key={c.key}
                    className={[
                      c.align === 'center' ? 'is-center' : '',
                      c.band && row.maxOccupancy !== null ? `pms-occ is-${row.maxOccupancy}` : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    {cell(row, c.key)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          {data !== null && rows.length > 0 && multi && (
            <tfoot>
              <tr>
                <td colSpan={columns.length} className="pms-cfg-foot">
                  {/* Per établissement, never one total (M11′ 2): a capacity is
                      one building's figure. */}
                  {reach
                    .map((b) => {
                      const mine = rows.filter((r) => r.propertyCode === b.property.code);
                      if (mine.length === 0) return null;
                      const persons = mine.reduce((s, r) => s + (r.maxOccupancy ?? 0), 0);
                      return `${b.property.labelFr} : ${mine.length} logement${mine.length > 1 ? 's' : ''} · ${persons} personnes max`;
                    })
                    .filter((t): t is string => t !== null)
                    .join(' — ')}
                </td>
              </tr>
            </tfoot>
          )}
          {data !== null && rows.length > 0 && !multi && (
            <tfoot>
              <tr>
                <td>
                  <strong>{rows.length}</strong>
                </td>
                <td className="is-center">
                  <strong>{capacity}</strong>
                </td>
                <td colSpan={columns.length - 2} className="pms-cfg-foot">
                  {/* « logements » is masculine, « chambres » feminine — the
                      participle has to follow, so noun and agreement travel
                      together rather than being concatenated blindly. */}
                  {filtered
                    ? `${apartments ? 'logements filtrés' : 'chambres filtrées'} sur ${allRows.length} · `
                    : `${apartments ? 'logements' : 'chambres'} · `}
                  {capacity} personnes max
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {picker}
      {createIn && (
        <RoomCreate
          apartments={createIn.bundle.property.kind === 'apartments'}
          roomTypes={createIn.types}
          bedConfigs={createIn.bedConfigs}
          saving={saving}
          error={saveErr}
          onCreate={(draft) => void create(draft)}
          onClose={() => {
            setCreateIn(null);
            setSaveErr('');
          }}
        />
      )}

      {openRoom && openSource && (
        <RoomSheet
          room={openRoom}
          roomTypes={openSource.types}
          bedConfigs={openSource.bedConfigs}
          bedTypes={openSource.bedTypes}
          beds={openRoomBeds}
          canEdit={canEdit}
          saving={saving}
          error={saveErr}
          onSave={(patch) => void save(patch)}
          onDelete={() => void remove()}
          onClose={() => {
            setOpenRoomId(null);
            setSaveErr('');
          }}
        />
      )}

      {/* The catalogue shares this screen but not its subject: above is the
          physical configuration of the units, below is what the desk may sell
          alongside them. Its own component for that reason, and because this
          file is already long. Unlike the table above, both are about ONE
          établissement — a catalogue, a channel mapping — so they take their
          établissement from a choice of their own (U3). */}
      <PerEtablissement>
        <Catalogue />
        <Conditions />
        <ChannexMapping />
      </PerEtablissement>
    </div>
  );
}
